import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCotisationsByMembrePublic, getPaiementsByMembrePublic } from '../services/api';
import NotificationBell from '../components/NotificationBell';
import CarteMembre from '../components/CarteMembre';
import Footer from '../components/Footer';
import DebugLogs from '../components/DebugLogs';
import InstallPrompt from '../components/InstallPrompt';
import { debugLog } from '../utils/debugLogger';
import './DashboardMembre.css';

const DashboardMembre = () => {
  const navigate = useNavigate();
  const [membre, setMembre] = useState(null);
  const [cotisations, setCotisations] = useState([]);
  const [paiements, setPaiements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false); // Flag pour éviter les rechargements multiples
  const [showCarte, setShowCarte] = useState(false);
  const [redirectAttempted, setRedirectAttempted] = useState(false); // Flag pour éviter les redirections multiples

  useEffect(() => {
    // Attendre un peu pour que localStorage soit prêt (surtout après une redirection depuis login)
    const checkMembreData = () => {
      // Afficher les données de la dernière connexion depuis sessionStorage pour le débogage
      const lastLoginData = sessionStorage.getItem('last_login_data');
      if (lastLoginData) {
        try {
          const loginData = JSON.parse(lastLoginData);
          console.log('📋 DashboardMembre - Données de la dernière connexion (sessionStorage):');
          console.log('   Timestamp:', loginData.timestamp);
          console.log('   MembreData:', loginData.membreData);
          console.log('   Stored:', loginData.stored);
        } catch (e) {
          console.error('Erreur lors du parsing des données de session:', e);
        }
      }
      
      // Lire les données du membre depuis localStorage (vérifier plusieurs fois si nécessaire)
      let membreData = localStorage.getItem('membre_user');
      console.log('📋 DashboardMembre - Données depuis localStorage:', membreData);
      console.log('📋 DashboardMembre - Type de membreData:', typeof membreData);
      console.log('📋 DashboardMembre - Longueur de membreData:', membreData?.length);
      
      // Si pas de données, réessayer après un court délai (localStorage pourrait ne pas être prêt)
      if (!membreData) {
        console.log('⚠️ Aucune donnée membre trouvée, attente de 200ms avant nouvelle vérification...');
        console.log('   localStorage.getItem("membre_user"):', localStorage.getItem('membre_user'));
        console.log('   Tous les clés localStorage:', Object.keys(localStorage));
        
        // Réessayer après 200ms
        setTimeout(() => {
          membreData = localStorage.getItem('membre_user');
          if (!membreData && !redirectAttempted) {
            console.log('❌ Toujours aucune donnée après attente, redirection vers login');
            setRedirectAttempted(true);
            setTimeout(() => {
              navigate('/login-membre', { replace: true });
            }, 300);
          } else if (membreData) {
            // Si on trouve les données maintenant, continuer le traitement
            processMembreData(membreData);
          }
        }, 200);
        return;
      }
      
      // Si on a les données, les traiter
      processMembreData(membreData);
    };
    
    const processMembreData = (membreData) => {

    try {
      const parsedMembre = JSON.parse(membreData);
      console.log('✅ Membre parsé:', parsedMembre);
      console.log('   - ID:', parsedMembre.id);
      console.log('   - Numéro matricule:', parsedMembre.numero_matricule);
      
    // Vérifier si le membre est valide (ID ou numéro matricule)
      console.log('🔍 Vérification du membre:');
      console.log('   - parsedMembre existe?', !!parsedMembre);
      console.log('   - parsedMembre.id:', parsedMembre.id, '(type:', typeof parsedMembre.id, ')');
      console.log('   - parsedMembre.numero_matricule:', parsedMembre.numero_matricule);
      console.log('   - Condition ID:', !!parsedMembre.id);
      console.log('   - Condition numero_matricule:', !!parsedMembre.numero_matricule);
      console.log('   - Condition combinée:', !!(parsedMembre.id || parsedMembre.numero_matricule));
      
      // Vérifier si le membre a au moins un numéro matricule (plus important que l'ID)
      // L'ID peut être 0 ou manquant, mais le numéro matricule est obligatoire
      const hasMatricule = parsedMembre.numero_matricule && 
                          String(parsedMembre.numero_matricule).trim() !== '';
      
      // Vérifier aussi si l'objet membre a des propriétés valides
      const hasValidMembreData = parsedMembre && 
                                 typeof parsedMembre === 'object' &&
                                 Object.keys(parsedMembre).length > 0;
      
      console.log('🔍 Vérification détaillée:');
      console.log('   - hasMatricule:', hasMatricule, '(matricule:', parsedMembre.numero_matricule, ')');
      console.log('   - hasValidMembreData:', hasValidMembreData);
      console.log('   - Clés disponibles:', Object.keys(parsedMembre || {}));
      
      // Si le membre n'a pas de numéro matricule ou n'est pas un objet valide, rediriger vers login
      if (!hasValidMembreData || !hasMatricule) {
        console.error('❌ Membre non valide (pas de numéro matricule ou données invalides)');
        console.error('   Données complètes:', parsedMembre);
        console.error('   Type de parsedMembre:', typeof parsedMembre);
        console.error('   Numéro matricule valeur:', parsedMembre?.numero_matricule);
        // Attendre plus longtemps avant de rediriger pour éviter les redirections trop rapides
        if (!redirectAttempted) {
          setRedirectAttempted(true);
          setTimeout(() => {
            navigate('/login-membre', { replace: true });
          }, 1000); // Augmenté à 1 seconde
        }
        return;
      }
      
      // S'assurer que l'ID est un nombre si présent (mais ce n'est pas obligatoire)
      if (parsedMembre.id !== null && parsedMembre.id !== undefined && parsedMembre.id !== '') {
        if (typeof parsedMembre.id !== 'number') {
          const idAsNumber = parseInt(parsedMembre.id, 10);
          if (!isNaN(idAsNumber)) {
            parsedMembre.id = idAsNumber;
            console.log('   - ID converti en nombre:', parsedMembre.id);
          }
        }
      } else {
        // Si pas d'ID, ce n'est pas grave, on peut quand même afficher le dashboard
        console.log('   - Pas d\'ID, mais numéro matricule présent:', parsedMembre.numero_matricule);
      }
      
      console.log('✅ Membre valide, définition du state');
      console.log('   - ID final:', parsedMembre.id, '(type:', typeof parsedMembre.id, ')');
      console.log('   - Numéro matricule final:', parsedMembre.numero_matricule);
      console.log('   - Toutes les clés:', Object.keys(parsedMembre));
      
      // Définir le membre et garder le loading actif pendant le chargement des données
      setMembre(parsedMembre);
      setDataLoaded(false); // Réinitialiser le flag lors d'un nouveau membre
      // Ne pas mettre setLoading(false) ici, laisser loadData le faire
    } catch (error) {
      console.error('❌ Erreur lors du parsing des données membre:', error);
      console.error('   Données brutes:', membreData);
      if (!redirectAttempted) {
        setRedirectAttempted(true);
        setTimeout(() => {
          navigate('/login-membre', { replace: true });
        }, 500);
      }
      return;
    }
    };
    
    // Appeler la fonction de vérification
    checkMembreData();
  }, [navigate, redirectAttempted]);

  const loadData = useCallback(async () => {
    if (!membre) {
      console.error('❌ Impossible de charger les données: membre manquant');
      setLoading(false);
      return;
    }
    
    // Utiliser le numéro matricule pour charger les données (même si pas d'ID)
    const numeroMatricule = membre.numero_matricule;
    if (!numeroMatricule) {
      console.warn('⚠️ Membre sans numéro matricule, impossible de charger cotisations et paiements');
      setCotisations([]);
      setPaiements([]);
      setLoading(false);
      return;
    }
    
    try {
      console.log('📥 Chargement des cotisations et paiements pour membre:', numeroMatricule);
      debugLog('DashboardMembre - Chargement des cotisations et paiements pour membre:', {
        id: membre.id,
        numero_matricule: numeroMatricule
      });
      const [cotisationsRes, paiementsRes] = await Promise.all([
        getCotisationsByMembrePublic(numeroMatricule),
        getPaiementsByMembrePublic(numeroMatricule)
      ]);
      console.log('✅ Cotisations chargées:', cotisationsRes.data?.length || 0);
      console.log('✅ Paiements chargés:', paiementsRes.data?.length || 0);
      setCotisations(cotisationsRes.data || []);
      setPaiements(paiementsRes.data || []);
      setDataLoaded(true); // Marquer comme chargé après succès
    } catch (err) {
      console.error('❌ Erreur lors du chargement des données:', err);
      console.error('   Détails:', err.response?.data || err.message);
      // Même en cas d'erreur, on affiche le dashboard avec des listes vides
      setCotisations([]);
      setPaiements([]);
    } finally {
      setLoading(false);
    }
  }, [membre]);

  useEffect(() => {
    if (!membre) {
      setLoading(false); // Pas de membre, arrêter le chargement
      return; // Attendre que le membre soit chargé
    }
    
    // Vérifier si on a déjà un numéro matricule pour éviter les appels inutiles
    if (!membre.numero_matricule) {
      console.warn('⚠️ Membre sans numéro matricule, impossible de charger les données');
      setLoading(false);
      setCotisations([]);
      setPaiements([]);
      return;
    }
    
    // Ne charger qu'une seule fois par numéro matricule
    if (dataLoaded) {
      console.log('📊 Données déjà chargées pour ce membre, pas de rechargement');
      return;
    }
    
    console.log('🔄 Chargement des données pour le membre:', membre.id || membre.numero_matricule);
    setDataLoaded(true); // Marquer comme chargé avant l'appel
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membre?.numero_matricule]); // Utiliser seulement le numéro matricule comme dépendance pour éviter les boucles

  const handleLogout = () => {
    localStorage.removeItem('membre_user');
    navigate('/login-membre');
  };

  const getMoisNom = (mois) => {
    const moisNoms = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return moisNoms[mois - 1] || mois;
  };

  const cotisationsImpayees = cotisations.filter(c => c.statut === 'impaye');
  const cotisationsPayees = cotisations.filter(c => c.statut === 'paye');
  // Un membre est "en ordre" seulement s'il a au moins une cotisation ET toutes sont payées
  const enOrdre = cotisations.length > 0 && cotisationsImpayees.length === 0;
  
  // Calculer le total des montants payés
  const totalMontantPaye = paiements.reduce((total, paiement) => {
    return total + parseFloat(paiement.montant || 0);
  }, 0);

  if (loading || !membre) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img src="/logo.png" alt="Logo" style={{ height: '50px', width: '50px', objectFit: 'contain' }} />
            <div>
              <h1>MUTU NDE ESPOIR ASBL</h1>
              <p>Bienvenue, {membre.nom || ''} {membre.postnom || ''} {membre.prenom || ''}</p>
              <p>Matricule: {membre.numero_matricule || 'N/A'}</p>
            </div>
          </div>
          <div className="header-actions">
            <NotificationBell />
            <button className="btn btn-danger" onClick={handleLogout}>
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Centre de santé - Mise en valeur */}
        <div style={{
          padding: '20px',
          backgroundColor: '#fff3e0',
          borderRadius: '10px',
          border: '2px solid #ff9800',
          boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
          marginBottom: '25px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '14px', color: '#e65100', marginBottom: '8px', fontWeight: 'bold' }}>
            🏥 CENTRE DE SANTÉ
          </div>
          <div style={{ fontSize: '24px', color: '#e65100', fontWeight: 'bold', whiteSpace: 'nowrap', marginBottom: '5px' }}>
            {membre.centre_sante || '-'}
          </div>
          <div style={{ fontSize: '12px', color: '#f57c00', fontStyle: 'italic' }}>
            Lieu de suivi des soins
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <h3>Statut</h3>
            <div className="value">
              {enOrdre ? (
                <span className="badge badge-success">En ordre</span>
              ) : (
                <span className="badge badge-danger">
                  {cotisations.length === 0 
                    ? 'Aucune cotisation' 
                    : `${cotisationsImpayees.length} impayée(s)`}
                </span>
              )}
            </div>
          </div>

          <div className="stat-card stat-card-success">
            <h3>Cotisations payées</h3>
            <div className="value value-success">{cotisationsPayees.length}</div>
          </div>

          <div className="stat-card stat-card-info">
            <h3>Total paiements</h3>
            <div className="value value-info">{paiements.length}</div>
          </div>

          <div className="stat-card stat-card-highlight">
            <h3>Total montant payé</h3>
            <div className="value value-highlight">
              {totalMontantPaye.toLocaleString('fr-FR', { 
                minimumFractionDigits: 0, 
                maximumFractionDigits: 0 
              })} CDF
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Mes cotisations</h2>
          <table className="table">
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>Mois/Année</th>
                <th style={{ whiteSpace: 'nowrap' }}>Semaine</th>
                <th style={{ whiteSpace: 'nowrap' }}>Montant</th>
                <th style={{ whiteSpace: 'nowrap' }}>Statut</th>
                <th style={{ whiteSpace: 'nowrap' }}>Date paiement</th>
              </tr>
            </thead>
            <tbody>
              {cotisations.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center' }}>
                    Aucune cotisation enregistrée
                  </td>
                </tr>
              ) : (
                cotisations.map(cotisation => (
                  <tr key={cotisation.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{getMoisNom(cotisation.mois)} {cotisation.annee}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{cotisation.semaine ? `Semaine ${cotisation.semaine}` : '-'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{parseFloat(cotisation.montant).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {cotisation.statut === 'paye' ? (
                        <span className="badge badge-success">Payée</span>
                      ) : (
                        <span className="badge badge-danger">Impayée</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{cotisation.date_paiement || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="card">
          <h2>Mes paiements</h2>
          <table className="table">
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>Date</th>
                <th style={{ whiteSpace: 'nowrap' }}>Montant</th>
                <th style={{ whiteSpace: 'nowrap' }}>Type</th>
                <th style={{ whiteSpace: 'nowrap' }}>Semaine</th>
                <th style={{ whiteSpace: 'nowrap' }}>N° Reçu</th>
              </tr>
            </thead>
            <tbody>
              {paiements.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center' }}>
                    Aucun paiement enregistré
                  </td>
                </tr>
              ) : (
                paiements.map(paiement => (
                  <tr key={paiement.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{paiement.date_paiement}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{paiement.montant} CDF</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {paiement.type_paiement === 'cotisation_initial' && 'Cotisation initiale'}
                      {paiement.type_paiement === 'cotisation_mensuelle' && 'Cotisation mensuelle'}
                      {paiement.type_paiement === 'autre' && 'Frais de carte'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {paiement.semaine ? `Semaine ${paiement.semaine}` : '-'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{paiement.numero_recu}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0 }}>Mes informations</h2>
            <button 
              className="btn btn-primary"
              onClick={() => setShowCarte(true)}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              <span>📄</span>
              <span>Télécharger ma carte</span>
            </button>
          </div>
          
          {/* Photo du membre */}
          {membre.photo && (
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <img 
                src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${membre.photo}`}
                alt={`${membre.nom} ${membre.prenom}`}
                style={{
                  width: '150px',
                  height: '150px',
                  objectFit: 'cover',
                  borderRadius: '10px',
                  border: '3px solid #2c3e50',
                  cursor: 'pointer'
                }}
                onClick={() => window.open(`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${membre.photo}`, '_blank')}
                title="Cliquez pour agrandir"
              />
            </div>
          )}

          {/* Informations personnelles */}
          <h3 style={{ marginTop: '20px', marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', paddingBottom: '10px' }}>Informations personnelles</h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '15px',
            marginBottom: '20px'
          }}>
            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: 'bold' }}>NOM COMPLET</div>
              <div style={{ fontSize: '16px', color: '#2c3e50', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {membre.nom} {membre.postnom || ''} {membre.prenom}
              </div>
            </div>

            <div style={{
              padding: '15px',
              backgroundColor: '#e3f2fd',
              borderRadius: '8px',
              border: '1px solid #90caf9'
            }}>
              <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '5px', fontWeight: 'bold' }}>NUMÉRO MATRICULE</div>
              <div style={{ fontSize: '18px', color: '#1565c0', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {membre.numero_matricule || '-'}
              </div>
            </div>

            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: 'bold' }}>DATE DE NAISSANCE</div>
              <div style={{ fontSize: '16px', color: '#2c3e50', whiteSpace: 'nowrap' }}>
                {membre.date_naissance || '-'}
              </div>
            </div>

            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: 'bold' }}>LIEU DE NAISSANCE</div>
              <div style={{ fontSize: '16px', color: '#2c3e50', whiteSpace: 'nowrap' }}>
                {membre.lieu_naissance || '-'}
              </div>
            </div>

            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: 'bold' }}>DATE D'ADHÉSION</div>
              <div style={{ fontSize: '16px', color: '#2c3e50', whiteSpace: 'nowrap' }}>
                {membre.date_adhesion || '-'}
              </div>
            </div>

            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: 'bold' }}>PROFESSION</div>
              <div style={{ fontSize: '16px', color: '#2c3e50', whiteSpace: 'nowrap' }}>
                {membre.profession || '-'}
              </div>
            </div>
          </div>

          {/* Informations de contact */}
          <h3 style={{ marginTop: '30px', marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', paddingBottom: '10px' }}>Informations de contact</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '15px',
            marginBottom: '20px'
          }}>
            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: 'bold' }}>TÉLÉPHONE</div>
              <div style={{ fontSize: '16px', color: '#2c3e50', whiteSpace: 'nowrap' }}>
                {membre.telephone || '-'}
              </div>
            </div>

            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: 'bold' }}>AVENUE, NUMÉRO, QUARTIER</div>
              <div style={{ fontSize: '16px', color: '#2c3e50', whiteSpace: 'nowrap' }}>
                {membre.quartier || '-'}
              </div>
            </div>

            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: 'bold' }}>COMMUNE</div>
              <div style={{ fontSize: '16px', color: '#2c3e50', whiteSpace: 'nowrap' }}>
                {membre.commune || '-'}
              </div>
            </div>
          </div>

          {/* Informations organisationnelles */}
          <h3 style={{ marginTop: '30px', marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', paddingBottom: '10px' }}>Informations organisationnelles</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '15px',
            marginBottom: '20px'
          }}>
            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: 'bold' }}>ASSOCIATION</div>
              <div style={{ fontSize: '16px', color: '#2c3e50', whiteSpace: 'nowrap' }}>
                {membre.association || '-'}
              </div>
            </div>

            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: 'bold' }}>RÉCOLTEUR</div>
              <div style={{ fontSize: '16px', color: '#2c3e50', whiteSpace: 'nowrap' }}>
                {membre.nom_recolteur || '-'}
              </div>
            </div>
          </div>

          {/* Informations familiales */}
          <h3 style={{ marginTop: '30px', marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', paddingBottom: '10px' }}>Informations familiales</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '15px',
            marginBottom: '20px'
          }}>
            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: 'bold' }}>NOM DE L'ÉPOUSE/ÉPOUX</div>
              <div style={{ fontSize: '16px', color: '#2c3e50', whiteSpace: 'nowrap' }}>
                {membre.nom_epouse || '-'}
              </div>
            </div>

            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: 'bold' }}>NOMBRE D'ENFANTS</div>
              <div style={{ fontSize: '16px', color: '#2c3e50', whiteSpace: 'nowrap' }}>
                {membre.nombre_enfants || '0'}
              </div>
            </div>

            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
              gridColumn: 'span 1'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: 'bold' }}>NOMS DES ENFANTS</div>
              <div style={{ 
                fontSize: '16px', 
                color: '#2c3e50', 
                whiteSpace: 'pre-wrap',
                minHeight: '60px',
                wordBreak: 'break-word'
              }}>
                {membre.noms_enfants || '-'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal pour la carte de membre */}
      {showCarte && (
        <CarteMembre 
          membre={membre} 
          onClose={() => setShowCarte(false)} 
        />
      )}

      <Footer />
      <DebugLogs />
      <InstallPrompt />
    </div>
  );
};

export default DashboardMembre;


